import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Utensils, Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-900">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-slate-900 p-12 lg:flex">
        <img
          src="https://images.unsplash.com/photo-1707324148764-99647364afa3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG9yYW5nZSUyMGxvZ298ZW58MHx8fHwxNzg3MTU4Mzc3fDA&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-600 text-white"><Utensils className="h-6 w-6" /></div>
          <div>
            <div className="font-heading text-xl font-extrabold text-white">SocietyBites</div>
            <div className="text-sm text-orange-400">Finance Portal</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white">Know your numbers.<br />Grow your business.</h1>
          <p className="mt-4 max-w-md text-slate-300">Track revenue, expenses and profit allocation for SocietyBites in one clean dashboard.</p>
        </div>
        <div className="relative text-xs text-slate-500">© 2026 SocietyBites. Internal finance tool.</div>
      </div>

      <div className="flex w-full items-center justify-center bg-slate-50 p-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-600 text-white"><Utensils className="h-6 w-6" /></div>
              <div>
                <div className="font-heading text-xl font-extrabold text-slate-900">SocietyBites</div>
                <div className="text-sm text-orange-600">Finance Portal</div>
              </div>
            </div>
          </div>
          <h2 className="font-heading text-3xl font-bold text-slate-900">Welcome back</h2>
          <p className="mt-1 text-slate-500">Sign in to your finance portal</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" placeholder="you@societybites.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required className="bg-white" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required className="bg-white" />
            </div>
            {error && <div className="rounded-lg bg-rose-50 px-4 py-2.5 text-sm text-rose-600" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full gap-2 bg-orange-600 py-6 text-base font-semibold hover:bg-orange-700" data-testid="login-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
